import React from 'react';
import { useExperiments } from '@wix/wix-experiments-react';
import { GlobalDesignGroupActionPanel } from '../types';
import { ShadowControlsPS } from './components/ShadowControlsPS';
import { ShadowPresets } from './components/ShadowPresets';
import { useGlobalDesignStyles } from '../common/abstractStyles';
import { Box } from '@wix/design-system';
import { useBI } from '../hooks/useBI';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { getMostSpecificValueFromDecomposed } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { GD_WDS_EXPERIMENT } from '../experiments';
import { useTranslation } from '../..';
import { GLOBAL_DESIGN_TRANSLATION_KEYS } from '../constants';
import { resolveEditorComponent } from '../resolveEditorComponent';
import { EditorSubType } from '@wix/editor-platform-sdk-types';
import { getCurrentDecomposedValues } from '../common/abstractStyles/decompositionUtils';

const ResolvedShadowControls = resolveEditorComponent({
  default: () => import('./components/ShadowControls'),
  overrides: {
    [EditorSubType.Studio2]: () => import('./components/ShadowControlsStudio2'),
  },
});

const ShadowStyles: GlobalDesignGroupActionPanel = (props) => {
  const { experiments } = useExperiments({ readOnly: true });
  const isWDS = experiments.enabled(GD_WDS_EXPERIMENT);
  const [t] = useTranslation();

  const { values, updateStyles, isLoading, sdkClient } = useGlobalDesignStyles(
    props.host,
    'shadowStyles',
    props.componentsAPI,
    { withVariablesResolution: true },
  );
  const { trackPresetApply } = useBI();

  if (isLoading || !values) {
    return <div>{t(GLOBAL_DESIGN_TRANSLATION_KEYS.LOADING)}</div>;
  }

  const currentDecomposed = getCurrentDecomposedValues(values);

  const handlePresetApplied = async (presetId: string) => {
    const newValues = props.stylePresets?.find(
      (preset) => preset.id === presetId,
    )?.values;

    if (newValues) {
      trackPresetApply(StylesDerivedNativeActions.shadowStyles, {
        prevValues: JSON.stringify(
          getMostSpecificValueFromDecomposed(currentDecomposed),
        ),
        newValues: JSON.stringify(newValues),
        targetComponents: props.group.metadata.targetComponents,
      });
      await updateStyles(newValues);
      await props.onPresetApplied?.(presetId);
    }
  };

  const presets =
    props.stylePresets && props.stylePresets.length > 0 ? (
      <ShadowPresets
        presets={props.stylePresets}
        metadata={values.metadata}
        currentDecomposed={currentDecomposed}
        onPresetApplied={handlePresetApplied}
      />
    ) : null;

  return (
    <Box direction="vertical" width="100%" gap="SP4">
      {presets}
      {isWDS ? (
        <ResolvedShadowControls
          values={values}
          updateStyles={updateStyles}
          metadata={props.group.metadata}
          sdkClient={sdkClient}
        />
      ) : (
        <ShadowControlsPS
          values={values}
          host={props.host}
          updateStyles={updateStyles}
          metadata={props.group.metadata}
        />
      )}
    </Box>
  );
};

export default ShadowStyles;
